import React, { useState, useEffect } from "react"
import Select from "react-select"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { showToaster } from "components/Common/common"
import axiosInstance from "components/Common/ApiManagement/axiosInstance"
import { ApiNames, VIEW_TYPE_OPTIONS } from "components/Common/ApiManagement/apiServices"
import { useNavigate } from "react-router-dom"
import "./style.css"
import {
  FileText,
  Filter,
  Users,
  Building2,
  Eye,
  Save,
  ArrowLeft,
  Tag,
  Layers,
  Grid,
  AlertCircle
} from 'lucide-react';

const CustomReportLayout = props => {
  const navigate = useNavigate()

  const [reportName, setReportName] = useState("")
  const [areaName, setAreaName] = useState([])
  const [scanName, setScanName] = useState([])
  const [scanGroup, setScanGroup] = useState([])
  const [scanTitle, setScanTitle] = useState([])
  const [category, setCategory] = useState([])
  const [scanSubcat, setScanSubcat] = useState([])
  const [selectedViewTypes, setSelectedViewTypes] = useState([])
  const [viewUploadingLoad, setViewUploadLoad] = useState(false)
  const [assignTo, setAssignTo] = useState("everyone")
  const [organizationOptions, setOrganizationOptions] = useState([])
  const [selectedOrganizations, setSelectedOrganizations] = useState([])
  const [hasUserEdited, setHasUserEdited] = useState(false);

  const [optionsData, setOptionsData] = useState({
    area: [],
    scanGroup: [],
    scanTitle: [],
    category: [],
    scanSubcat: [],
    scanName: [],
  })

  useEffect(() => {
    props.setBreadcrumbItems("Custom Report Layout", [])
    fetchFilterOptions()
    fetchLastSavedConfig()
    fetchOrganizations()
  }, [])

  const fetchOrganizations = () => {
    axiosInstance
      .get(ApiNames.LIST_ORGANIZATION)
      .then(res => {
        const options = res.data.data.map(org => ({
          label: org.branch,
          value: org._id,
        }))
        setOrganizationOptions(options)
      })
      .catch(err => console.error("Error fetching organizations:", err))
  }

  const fetchLastSavedConfig = async () => {
    try {
      const res = await axiosInstance.get(ApiNames.LAST_SAVED_VIEW_CONFIG);
      const data = res?.data?.data;
      if (!data) return;

      setReportName(data.reportName || "");
      if (!hasUserEdited) {
        setAssignTo(data.assignTo || "everyone");

        if (data.assignTo === "organization" && data.assignedOrganizations) {
          setSelectedOrganizations(
            data.assignedOrganizations.map(org => ({
              label: org.name,
              value: org.id,
            }))
          );
        } else {
          setSelectedOrganizations([]);
        }
      }

      fetchViewTypes(data.reportName || "");
    } catch (err) {
      console.error("Error fetching last saved config", err);
    }
  };

  const fetchViewTypes = async name => {
    try {
      if (!name.trim()) return;

      const res = await axiosInstance.post(ApiNames.VIEW_METHOD_GET, {
        reportName: name.trim(),
      });

      const data = res?.data?.data;
      if (!data) return;

      setSelectedViewTypes(
        (data.viewTypes || [])
          .map(v => VIEW_TYPE_OPTIONS.find(option => option.value === v))
          .filter(Boolean)
      );
      setAreaName((data.areaName || []).map(v => ({ label: v, value: v })));
      setScanGroup((data.scanGroup || []).map(v => ({ label: v, value: v })));
      setScanTitle((data.scanTitle || []).map(v => ({ label: v, value: v })));
      setCategory((data.category || []).map(v => ({ label: v, value: v })));
      setScanSubcat((data.scanSubcat || []).map(v => ({ label: v, value: v })));
      setScanName((data.scanName || []).map(v => ({ label: v, value: v })));
    } catch (err) {
      console.error("Error fetching view config", err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await axiosInstance.post(ApiNames.CUSTOM_REPORT_LIST)
      if (res.data?.data) {
        const { areas, scanGroup, scanTitle, category, scanSubcat, scanName } = res.data.data

        setOptionsData({
          area: areas.map(val => ({ label: val, value: val })),
          scanGroup: scanGroup.map(val => ({ label: val, value: val })),
          scanTitle: scanTitle.map(val => ({ label: val, value: val })),
          category: category.map(val => ({ label: val, value: val })),
          scanSubcat: scanSubcat.map(val => ({ label: val, value: val })),
          scanName: scanName.map(val => ({ label: val, value: val })),
        })
      }
    } catch (err) {
      console.error("Error fetching compliance filters", err)
    }
  }

  const handleUpload = async () => {
    if (!reportName.trim() || selectedViewTypes.length === 0) {
      return showToaster(0, "Report name and at least one View Type are required")
    }
    if (assignTo === "organization" && selectedOrganizations.length === 0) {
      return showToaster(0, "Please select at least one organization");
    }

    if (
      areaName.length === 0 &&
      scanName.length === 0 &&
      scanGroup.length === 0 &&
      category.length === 0 &&
      scanSubcat.length === 0
    ) {
      return showToaster(
        0,
        "Please select at least one option from Compliance Areas, Scan Name, Scan Group, Category, or Sub-Category"
      )
    }

    setViewUploadLoad(true)
    try {
      const res = await axiosInstance.post(ApiNames.VIEW_METHOD_UPLOAD, {
        reportName: reportName.trim(),
        areaName: areaName.map(a => a.value),
        scanGroup: scanGroup.map(a => a.value),
        scanTitle: scanTitle.map(a => a.value),
        category: category.map(a => a.value),
        scanSubcat: scanSubcat.map(a => a.value),
        scanName: scanName.map(a => a.value),
        viewTypes: selectedViewTypes.map(v => v.value),
        assignTo,
        assignedOrganizations:
          assignTo === "organization"
            ? selectedOrganizations.map(org => ({
                id: org.value,
                name: org.label,
              }))
            : [],
      })

      if (res.data.status) {
        showToaster(1, "View type(s) saved successfully")
        // navigate('/Savedreportlist')
      } else {
        showToaster(0, "Upload failed")
      }
    } catch (err) {
      console.error("Upload error:", err)
      showToaster(0, "Upload failed")
    } finally {
      setViewUploadLoad(false)
    }
  }

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: '0.375rem 0.5rem',
      border: `2px solid ${state.isFocused ? '#3b82f6' : '#e2e8f0'}`,
      borderRadius: '10px',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    multiValue: (base) => ({
      ...base,
      background: '#eff6ff',
      borderRadius: '6px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#3b82f6',
      fontWeight: '500',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#3b82f6',
      ':hover': {
        background: '#3b82f6',
        color: 'white',
      },
    }),
  }

  return (
    <div className="custom-report-page">
      {/* Header */}
      <div className="report-page-header">
        <div className="report-header-content">
          <div className="report-header-title-row">
            <FileText className="report-header-icon" />
            <h1>Custom Report Layout</h1>
          </div>
          <p className="report-header-subtitle">
            Configure custom compliance reports with filters and view types
          </p>
        </div>
        <button
          className="report-back-btn"
          onClick={() => navigate('/Savedreportlist')}
        >
          <ArrowLeft className="btn-icon" />
          Back to Reports
        </button>
      </div>

      {/* Main Content */}
      <div className="report-content-wrapper">
        <div className="report-main-card">
          {/* Report Name Section */}
          <div className="report-section">
            <div className="section-header">
              <Tag className="section-icon" />
              <h3>Report Information</h3>
            </div>
            <div className="form-group">
              <label className="form-label">Report Name *</label>
              <input
                type="text"
                className="form-input"
                value={reportName}
                onChange={e => setReportName(e.target.value)}
                placeholder="Enter a unique report name"
              />
            </div>
          </div>

          {/* Filter Options Section */}
          <div className="report-section">
            <div className="section-header">
              <Filter className="section-icon" />
              <h3>Filter Criteria</h3>
              <span className="section-badge">Select at least one</span>
            </div>

            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label">
                  <Grid className="label-icon" />
                  Compliance Areas
                </label>
                <Select
                  isMulti
                  value={areaName}
                  onChange={setAreaName}
                  options={optionsData.area}
                  placeholder="Select compliance areas..."
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Layers className="label-icon" />
                  Scan Name
                </label>
                <Select
                  isMulti
                  value={scanName}
                  onChange={setScanName}
                  options={optionsData.scanName}
                  placeholder="Select scan names..."
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Layers className="label-icon" />
                  Scan Group
                </label>
                <Select
                  isMulti
                  value={scanGroup}
                  onChange={setScanGroup}
                  options={optionsData.scanGroup}
                  placeholder="Select scan groups..."
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Tag className="label-icon" />
                  Category
                </label>
                <Select
                  isMulti
                  value={category}
                  onChange={setCategory}
                  options={optionsData.category}
                  placeholder="Select categories..."
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Tag className="label-icon" />
                  Scan Sub-Category
                </label>
                <Select
                  isMulti
                  value={scanSubcat}
                  onChange={setScanSubcat}
                  options={optionsData.scanSubcat}
                  placeholder="Select sub-categories..."
                  styles={customSelectStyles}
                />
              </div>
            </div>
          </div>

          {/* View Types Section */}
          <div className="report-section">
            <div className="section-header">
              <Eye className="section-icon" />
              <h3>View Types</h3>
              <span className="section-badge">Required</span>
            </div>
            <div className="form-group">
              <label className="form-label">Select View Types *</label>
              <Select
                isMulti
                value={selectedViewTypes}
                onChange={setSelectedViewTypes}
                options={VIEW_TYPE_OPTIONS}
                placeholder="Select one or more view types..."
                styles={customSelectStyles}
              />
            </div>
          </div>

          {/* Assignment Section */}
          <div className="report-section">
            <div className="section-header">
              <Users className="section-icon" />
              <h3>Assignment</h3>
            </div>

            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="assignTo"
                  value="everyone"
                  checked={assignTo === "everyone"}
                  onChange={e => {
                    setAssignTo(e.target.value);
                    setHasUserEdited(true);
                  }}
                />
                <span className="radio-custom"></span>
                <div className="radio-content">
                  <Users className="radio-icon" />
                  <div>
                    <div className="radio-title">Everyone</div>
                    <div className="radio-description">Available to all users</div>
                  </div>
                </div>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="assignTo"
                  value="organization"
                  checked={assignTo === "organization"}
                  onChange={e => {
                    setAssignTo(e.target.value);
                    setHasUserEdited(true);
                  }}
                />
                <span className="radio-custom"></span>
                <div className="radio-content">
                  <Building2 className="radio-icon" />
                  <div>
                    <div className="radio-title">Specific Organization</div>
                    <div className="radio-description">Restrict to selected organizations</div>
                  </div>
                </div>
              </label>
            </div>

            {assignTo === "organization" && (
              <div className="form-group org-select-group">
                <label className="form-label">
                  <Building2 className="label-icon" />
                  Select Organization(s) *
                </label>
                <Select
                  isMulti
                  value={selectedOrganizations}
                  onChange={setSelectedOrganizations}
                  options={organizationOptions}
                  placeholder="Select one or more organizations..."
                  styles={customSelectStyles}
                />
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="report-info-box">
            <div className="info-icon-wrapper">
              <AlertCircle className="info-icon" />
            </div>
            <div className="info-content">
              <h4>Configuration Requirements</h4>
              <ul>
                <li>Report name is required and must be unique</li>
                <li>At least one view type must be selected</li>
                <li>Select at least one filter criteria (Compliance Areas, Scan Name, etc.)</li>
                <li>If assigning to organizations, select at least one organization</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="report-actions">
            <button
              className={`save-btn ${viewUploadingLoad ? 'btn-loading' : ''}`}
              onClick={handleUpload}
              disabled={viewUploadingLoad}
            >
              {viewUploadingLoad ? (
                <>
                  <div className="btn-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="btn-icon" />
                  Save Layout
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(CustomReportLayout)
